<template>
  <div>
    <tbody class="text-grey-800 text-sm -mx-4" :key="editorKey">
      <tr class="border-grey-200" v-if="editing">
        <td class="py-3 px-6">
          <text-input
            v-model="subdomain.subdomain"
            class="mt-1"
            placeholder="Subdomain"
          />
          <text-input
            v-if="applicationOptions[0].value == 'ai'"
            v-model="subdomain.key"
            class="mt-1"
            placeholder="Whitelabel key"
          />
          <p class="mt-2 text-sm text-center text-danger">{{ errorMsg }}</p>
          <div
            v-if="applicationOptions[0].value == 'ai'"
            class="
              flex
              items-center
              mt-4
              text-sm
              bg-grey-50
              rounded-lg
              p-2
              text-grey-600
              font-semibold
            "
          >
            <InfoIcon class="mr-1" />
            {{ $t('frontend_sites_grooveai_subdomain_alert_text') }}
          </div>
        </td>
        <td class="py-3 px-6 text-left">
          <div v-if="applicationOptions.length > 1">
            <select
              v-model="subdomain.application"
              class="
                h-10
                outline-none
                rounded-lg
                border
                px-2
                border-grey-200
                mr-2
              "
              :value="subdomain.application"
              placeholder="Application"
            >
              <option
                v-for="(option, index) in applicationOptions"
                :value="option.value"
                :key="index"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
          <div v-else>
            {{ applicationOptions[0].name }}
          </div>
        </td>
        <td class="py-3 px-6 text-left">
          <div class="flex gap-2">
            <gd-button 
              variant="primary"
              size="icon"
              @click="save"
            >
              <check-icon />
            </gd-button>
            <gd-button 
              variant="outline"
              size="icon"
              @click="cancel"
              :disabled="saving"
            >
              <close-icon />
            </gd-button>
          </div>
        </td>
      </tr>
      <tr class="border-grey-200" v-else>
        <td class="py-3 px-6">
          https://<span class="font-bold text-primary">{{ $t('frontend_gduikit_addnewsubdomain') }}</span>.{{ domain.domain_name }}
        </td>
        <td class="py-3 px-6 text-left">
          <div>
            {{ applicationOptions[0].name }}
          </div>
        </td>
        <td class="py-3 px-6 text-left">
          <div class="flex">
            <gd-button
              variant="primary"
              size="icon"
              @click="showEditor"
            >
              <plus-icon />
            </gd-button>
          </div>
        </td>
      </tr>
    </tbody>
    <upgrade-modal ref="upgradeModal" />
  </div>
</template>

<script>
import Auth from '@groovepages/gd-lib/auth';
import { actions } from '@groovepages/gd-lib/userPermissions';
import { mapGetters } from 'vuex';
import CheckIcon from '../assets/check.svg';
import CloseIcon from '../assets/close-icon.svg';
import InfoIcon from '../assets/info-icon.svg';
import PlusIcon from '../assets/plus-icon.svg';
import GdButton from './Button';
import TextInput from './TextInput';
import UpgradeModal from './UpgradeModal';

export default {
  name: 'DomainSubdomainEditor',
  components: {
    GdButton,
    TextInput,
    UpgradeModal,
    CheckIcon,
    CloseIcon,
    PlusIcon,
    InfoIcon
  },
  computed: {
    ...mapGetters({
      domain: 'subdomains/domain',
      selectedApplication: 'subdomains/application'
    }),

    applicationOptions() {
      if (this.selectedApplication == 'GroovePages') {
        return [{ name: 'GroovePages', value: 'pages' }];
      }
      if (this.selectedApplication == 'GrooveSell') {
        return [
          { name: 'GrooveSell Branding', value: 'sell-funnel' },
          { name: 'GrooveSell Tracking', value: 'sell-tracking' }
        ];
      }
      if (this.selectedApplication == 'GrooveMember') {
        return [
          { name: 'GrooveMember Membership', value: 'membership' },
          { name: 'GrooveMember Portal', value: 'portal' }
        ];
      }
      if (this.selectedApplication == 'GrooveBlog') {
        return [{ name: 'GrooveBlog', value: 'blog' }];
      }
      if (this.selectedApplication == 'GrooveKart') {
        return [{ name: 'GrooveKart', value: 'kart' }];
      }
      if (this.selectedApplication == 'Groove.ai') {
        return [{ name: 'Groove.ai', value: 'ai' }];
      }

      return [{ name: this.selectedApplication, value: 'pages' }];
    }
  },
  props: {
    current: {
      type: Object,
      default: () => ({
        type: 2,
        subdomain: '',
        application: 'pages'
      })
    },
    saving: Boolean,
    editing: Boolean
  },
  watch: {
    editing() {
      this.editorKey += 1;
      if (
        !this.applicationOptions.some(
          e => e.value === this.subdomain.application
        )
      ) {
        this.subdomain.application = this.applicationOptions[0].value;
      }
    }
  },
  data() {
    return {
      subdomain: { ...this.current },
      editorKey: 1,
      errorMsg: ''
    };
  },
  created() {},
  mounted() {},
  methods: {
    showEditor() {
      this.$emit('showEditor');
    },
    save() {
      const userFeature = Auth.fetchFeatureValue(actions.SUB_DOMAINS);
      if (userFeature == 0) {
        this.$refs.upgradeModal.open();
      } else {
        this.errorMsg = '';
        const MIN_LENGTH = 3;
        const MAX_LENGTH = 63;
        const ALPHA_NUMERIC_REGEX = /^[a-z][a-z\-]*[a-z0-9]*$/;
        const START_END_HYPHEN_REGEX = /\A[^-].*[^-]\z/i;
        const reservedNames = [
          'www',
          'ftp',
          'mail',
          'pop',
          'smtp',
          'admin',
          'ssl',
          'sftp'
        ];
        if (reservedNames.includes(this.subdomain.subdomain)) {
          this.errorMsg = this.$t(
            'frontend_domain_dropdown_subdomain_cannot_be_reserved'
          );
          return false;
        }

        // if is too small or too big...
        if (
          this.subdomain.subdomain.length < MIN_LENGTH ||
          this.subdomain.subdomain.length > MAX_LENGTH
        ) {
          this.errorMsg = `${this.$t(
            'frontend_domain_dropdown_subdomain_must_have_a_length'
          )} ${MIN_LENGTH} ${this.$t('frontend_and')} ${MAX_LENGTH} ${this.$t(
            'frontend_domain_dropdown_characters'
          )}`;
          return false;
        }

        // if subdomain is started/ended with hyphen or is not alpha numeric
        if (!ALPHA_NUMERIC_REGEX.test(this.subdomain.subdomain)) {
          if (
            this.subdomain.subdomain.indexOf('-') === 0 ||
            this.subdomain.subdomain.indexOf('-') ===
              this.subdomain.subdomain.length - 1
          ) {
            this.errorMsg = this.$t(
              'frontend_domain_dropdown_subdomain_cannot_start'
            );
          } else {
            this.errorMsg = this.$t(
              'frontend_domain_dropdown_subdomain_must_be_alphanumeric'
            );
          }
          return false;
        }

        if (!this.subdomain.key && this.applicationOptions[0].value == 'ai') {
          this.errorMsg = this.$t(
            'frontend_domain_dropdown_subdomain_whitelabel_key_required'
          );
          return false;
        }

        this.$emit('save', this.subdomain);
      }
    },
    cancel() {
      this.$emit('cancel');
    }
  }
};
</script>
